import { Injectable } from '@angular/core';
import type {
    NormalizedBlockEntity,
    WebLegacyWidgetEntity,
    DenormalizedBlockEntity,
    DenormalizedSectionEntity,
    DenormalizedColumnEntity,
    NormalizedColumnEntity,
    NormalizedSectionEntity,
} from '@web/types/structure/structure.type';
import { Widgets } from '@common/enums';
import { CRM_PRODUCT_BLOCK_DISPLAY_ID, CRM_PRODUCT_WIDGET_DISPLAY_ID } from '@common/helpers';

@Injectable({
    providedIn: 'root',
})
export class SpecialBlocksService {
    public isCrmProductBlock(block: DenormalizedBlockEntity | NormalizedBlockEntity): boolean {
        return block.meta.displayId === CRM_PRODUCT_BLOCK_DISPLAY_ID;
    }

    public blockContainsProductWidget(block: DenormalizedBlockEntity): boolean {
        return (
            this.isBlockStructureEntity(block) &&
            block.meta.displayId === CRM_PRODUCT_BLOCK_DISPLAY_ID &&
            block.children.some((section) => this.sectionContainsProductWidget(section))
        );
    }

    public sectionContainsProductWidget(section: DenormalizedSectionEntity): boolean {
        return (
            this.isDenormalizedSection(section) &&
            section.children.some((column) => this.columnContainsProductWidget(column))
        );
    }

    public columnContainsProductWidget(column: DenormalizedColumnEntity): boolean {
        return this.isDenormalizedColumn(column) && column.children.some((widget) => this.isCrmProductWidget(widget));
    }

    public isCrmProductWidget(widget: WebLegacyWidgetEntity): boolean {
        if (!widget) return false;
        return widget.name === Widgets.PRODUCT_CARD && widget.displayId === CRM_PRODUCT_WIDGET_DISPLAY_ID;
    }

    /**
     * Whether an entity carries its children as entities rather than as references.
     *
     * One implementation for all three, since the only thing that varies is what a
     * reference looks like: a block and a row hold bare ids, while a column holds
     * the `{ id, schema }` pairs normalizr writes for its heterogeneous children.
     */
    private hasDenormalizedChildren(
        entity: { children?: unknown } | null,
        isReference: (child: unknown) => boolean
    ): boolean {
        const children = entity?.children;

        return Array.isArray(children) && children.length > 0 && !isReference(children[0]);
    }

    private static readonly isId = (child: unknown): boolean => typeof child === 'string';

    private static readonly isNormalizrRef = (child: unknown): boolean =>
        typeof child === 'object' && child !== null && 'schema' in child;

    public isBlockStructureEntity(
        block: NormalizedBlockEntity | DenormalizedBlockEntity
    ): block is DenormalizedBlockEntity {
        return this.hasDenormalizedChildren(block, SpecialBlocksService.isId);
    }

    public isDenormalizedSection(
        section: NormalizedSectionEntity | DenormalizedSectionEntity
    ): section is DenormalizedSectionEntity {
        return this.hasDenormalizedChildren(section, SpecialBlocksService.isId);
    }

    public isDenormalizedColumn(
        column: DenormalizedColumnEntity | NormalizedColumnEntity
    ): column is DenormalizedColumnEntity {
        return this.hasDenormalizedChildren(column, SpecialBlocksService.isNormalizrRef);
    }
}
